/**
 * A game's proof (docs/spec/06 §7): the game that was asked for and every input of every cave
 * attempt. The engine is deterministic, so replaying it (verify.ts) gives the scores the game
 * really made - a score sent without a log that makes it is not taken. Pure.
 */

import type { Input } from '../sim/types';
import { encodeInputs } from './replay';

export type LoggedGame =
  | { readonly mode: 'classic' | 'deep'; readonly players: 1 | 2; readonly startCave: string; readonly level: number }
  | { readonly mode: 'expedition'; readonly seed: number }
  | { readonly mode: 'daily'; readonly year: number; readonly month: number; readonly day: number };

/**
 * How an attempt ended: 'cave' - the cave ended it (won, died, out of time); 'restart' - the life
 * was given up from the pause menu; 'quit' - the game was ended there (only the last attempt).
 */
export type AttemptEnd = 'cave' | 'restart' | 'quit';

export interface LoggedAttempt {
  /** One input per scan, as `encodeInputs` writes them. */
  readonly inputs: string;
  readonly end: AttemptEnd;
}

export interface GameLog {
  readonly v: 1;
  readonly game: LoggedGame;
  readonly attempts: readonly LoggedAttempt[];
}

/** Far past any real game: a long session at level 5 is a few hundred attempts. */
export const MAX_ATTEMPTS = 1000;
/** Characters of one attempt's inputs: two per run of equal inputs, and a cave is at most a few thousand scans. */
export const MAX_ATTEMPT_CHARS = 16000;
export const START_CAVES = ['A', 'E', 'I', 'M'] as const;

/** Keeps the log of the game being played. */
export class GameRecorder {
  private readonly attempts: LoggedAttempt[] = [];
  private inputs: Input[] = [];

  constructor(private readonly game: LoggedGame) {}

  /** Note the input given to a scan; returns it, to pass on to `step`. */
  input(i: Input): Input {
    this.inputs.push(i);
    return i;
  }

  endAttempt(end: AttemptEnd): void {
    this.attempts.push({ inputs: encodeInputs(this.inputs), end });
    this.inputs = [];
  }

  /** The log as text to send. */
  text(): string {
    const log: GameLog = { v: 1, game: this.game, attempts: this.attempts };
    return JSON.stringify(log);
  }
}

const int = (v: unknown, lo: number, hi: number): v is number => Number.isInteger(v) && (v as number) >= lo && (v as number) <= hi;

function parseGame(raw: unknown): LoggedGame | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const g = raw as Record<string, unknown>;
  if (g['mode'] === 'classic' || g['mode'] === 'deep') {
    const { players, startCave, level } = g;
    if ((players !== 1 && players !== 2) || !int(level, 1, 5)) return null;
    if (!START_CAVES.some((c) => c === startCave)) return null;
    return { mode: g['mode'], players, startCave: String(startCave), level };
  }
  if (g['mode'] === 'expedition') return int(g['seed'], 0, 0xffffffff) ? { mode: 'expedition', seed: g['seed'] } : null;
  if (g['mode'] === 'daily') {
    const { year, month, day } = g;
    if (!int(year, 2026, 9999) || !int(month, 1, 12) || !int(day, 1, 31)) return null;
    return { mode: 'daily', year, month, day };
  }
  return null;
}

/** A log from outside, checked for shape and size; null if it is not one. */
export function parseGameLog(text: string): GameLog | null {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const game = parseGame(r['game']);
  const list = r['attempts'];
  if (r['v'] !== 1 || !game || !Array.isArray(list) || list.length < 1 || list.length > MAX_ATTEMPTS) return null;
  const attempts: LoggedAttempt[] = [];
  for (const a of list as unknown[]) {
    if (typeof a !== 'object' || a === null) return null;
    const { inputs, end } = a as Record<string, unknown>;
    if (typeof inputs !== 'string' || inputs.length > MAX_ATTEMPT_CHARS) return null;
    if (end !== 'cave' && end !== 'restart' && end !== 'quit') return null;
    attempts.push({ inputs, end });
  }
  return { v: 1, game, attempts };
}
